import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiFolder, FiRepeat, FiTag, FiPieChart, FiDollarSign, FiClock } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import {
  getIncomeStatisticApi,
  getIncomeProvisionalListApi,
  getIncomeComparativeListApi,
  getIncomePaidListApi,
} from "../../api/finance.income.api";
import { incomeStatusUi, formatMoney, formatVnd } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const SUBTLE = "#51607B";
const LIMIT = 10;
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "--/--/----");
const fmtK = (v) => String(Number(Number(v || 0).toFixed(4)));

const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ", sortBy: "createdAt", sortOrder: "desc" },
  { key: "OLDEST", label: "Cũ đến mới", sortBy: "createdAt", sortOrder: "asc" },
];
const FILTER_MODES = [
  { key: "DATE", label: "Ngày" },
  { key: "MONTH", label: "Tháng" },
  { key: "QUARTER", label: "Quý" },
];

// icon mini theo type (khớp typeStyleMap của app)
const STAT_COLORS = {
  project: { bg: "#EDFDF5", text: "#2ABA82", Icon: FiFolder },
  transaction: { bg: "#EFF6FF", text: "#2F80ED", Icon: FiRepeat },
  price: { bg: "#FFF7ED", text: "#F57127", Icon: FiTag },
  fee: { bg: "#F5F3FF", text: "#8B5CF6", Icon: FiPieChart },
  paid: { bg: "#EDFDF5", text: "#2ABA82", Icon: FiDollarSign },
  remain: { bg: "#F5F3FF", text: "#8B5CF6", Icon: FiClock },
};

const KIND = {
  "tam-tinh": {
    title: "Thu nhập tạm tính",
    api: getIncomeProvisionalListApi,
    provisional: true,
    summaryText: "TỔNG THU NHẬP TẠM TÍNH",
    summaryField: (s) => s.myProvisionalRemainingRevenueAfterTax,
    stats: [
      { sub: "DỰ ÁN", title: "Tổng số dự án", type: "project", kind: "count", get: (s, total) => s.totalProjectProvisional ?? s.totalProject ?? 0 },
      { sub: "GIAO DỊCH", title: "Tổng số giao dịch", type: "transaction", kind: "count", get: (s, total) => total },
      { sub: "GIÁ TRỊ NIÊM YẾT", title: "Giá trị niêm yết", type: "price", kind: "price", get: (s) => s.totalProvisionalValue },
      { sub: "GIÁ PDV TẠM TÍNH", title: "Giá tính PDV tạm tính", type: "fee", kind: "price", get: (s) => s.totalProvisionalServiceFee },
    ],
  },
  "doi-chieu": {
    title: "Thu nhập đối chiếu",
    api: getIncomeComparativeListApi,
    provisional: false,
    paymentStatus: "UNPAID",
    summaryText: "TỔNG THU NHẬP ĐỐI CHIẾU",
    summaryField: (s) => s.realRemainingRevenueAfterTax,
    stats: [
      { sub: "DỰ ÁN", title: "Tổng số dự án", type: "project", kind: "count", get: (s) => s.totalProject ?? 0 },
      { sub: "GIAO DỊCH", title: "Tổng số giao dịch", type: "transaction", kind: "count", get: (s, total) => total },
      { sub: "GIÁ TRỊ NIÊM YẾT", title: "Giá trị niêm yết", type: "price", kind: "price", get: (s) => s.totalValue },
      { sub: "GIÁ TÍNH PDV", title: "Giá tính PDV", type: "fee", kind: "price", get: (s) => s.priceForCommission },
    ],
  },
  "chi-tra": {
    title: "Thu nhập chi trả",
    api: getIncomePaidListApi,
    provisional: false,
    paymentStatus: "PARTIAL",
    summaryText: "TỔNG THU NHẬP ĐỐI CHIẾU",
    summaryField: (s) => s.realRemainingRevenueAfterTax,
    // App v2 (paid) ẩn 2 card GIÁ TRỊ NIÊM YẾT & GIÁ TÍNH PDV → chỉ hiển thị 4 card
    stats: [
      { sub: "DỰ ÁN", title: "Tổng số dự án", type: "project", kind: "count", get: (s) => s.totalProject ?? 0 },
      { sub: "GIAO DỊCH", title: "Tổng số giao dịch", type: "transaction", kind: "count", get: (s, total) => total },
      { sub: "GIẢI NGÂN", title: "PDV đã thanh toán", type: "paid", kind: "price", get: (s) => s.totalPaymentServiceFeePaid },
      { sub: "PDV CÒN LẠI", title: "PDV chưa thanh toán", type: "remain", kind: "price", get: (s) => s.totalPaymentServiceFeeUnpaid },
    ],
  },
};

const SummaryCard = ({ text, total }) => (
  <Box bg="white" border="1px solid" borderColor="#E5E9F0" borderRadius="12px" p="14px">
    <Text fontSize="12px" color={MUTED}>{text}</Text>
    <Text fontSize="20px" fontWeight={800} color={APP.primary200} mt="8px">{total}</Text>
  </Box>
);

const StatCard = ({ sub, title, value, type }) => {
  const c = STAT_COLORS[type] || STAT_COLORS.price;
  const Icon = c.Icon;
  return (
    <Box w="48%" minH="110px" borderRadius="8px" p="16px" border="1px solid" borderColor="#E5E9F0" bg="white">
      <Flex align="center" gap="12px">
        <Flex w="24px" h="24px" borderRadius="4px" bg={c.bg} align="center" justify="center">
          <Icon color={c.text} size={12} />
        </Flex>
        <Text fontSize="10px" fontWeight={600} color={TEXT}>{sub}</Text>
      </Flex>
      <Text fontSize="16px" fontWeight={700} color={TEXT} mt="4px">{value}</Text>
      <Text fontSize="12px" color={APP.gray} mt="4px">{title}</Text>
    </Box>
  );
};

const IncomeCard = ({ item, provisional, onClick }) => {
  const src = provisional ? item?.locking : item;
  const ui = incomeStatusUi(src);
  const title = provisional
    ? `${item?.projectName || ""}${item?.projectName ? " - " : ""}${item?.apartmentCode || ""}`.trim() || "-"
    : `${item?.project?.name || ""}${item?.project?.name ? " - " : ""}${item?.apartment?.code || ""}`.trim() || "-";
  const date = provisional ? item?.locking?.bookingTime : item?.bookingTime;
  const customer = provisional ? item?.customerName : item?.customer?.name;
  const feeLabel = provisional ? "Giá PDV tạm tính" : "Giá PDV";
  const feeValue = provisional ? item?.estimatedServiceFeePrice : item?.priceForCommission;
  const Row = ({ label, value, color }) => (
    <Flex gap="12px" align="flex-start" mt="8px">
      <Text fontSize="14px" color={SUBTLE} flex="0.42">{label}</Text>
      <Text fontSize="14px" fontWeight={600} color={color || TEXT} flex="0.58" textAlign="right" noOfLines={3}>{value}</Text>
    </Flex>
  );
  return (
    <Box as="button" type="button" onClick={onClick} textAlign="left" w="100%" bg="white" border="1px solid" borderColor="#E5E9F0" borderRadius="12px" p="14px">
      <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={2}>{title}</Text>
      <Flex align="center" gap="8px" mt="8px" flexWrap="wrap">
        <Text fontSize="14px" color={TEXT}>{fmtD(date)}</Text>
        <Box bg={ui.bg} borderRadius="6px" px="8px" py="3px">
          <Text fontSize="11px" fontWeight={600} color={ui.color}>{ui.label}</Text>
        </Box>
      </Flex>
      <Box h="1px" bg={BORDER} my="12px" />
      <Row label="Khách hàng" value={customer || "-"} />
      <Row label="Hệ số K" value={fmtK(item?.kRate)} />
      <Row label="Giá trị niêm yết" value={formatVnd(item?.totalValue || 0)} color={APP.primary200} />
      <Row label={feeLabel} value={formatVnd(feeValue || 0)} />
    </Box>
  );
};

const IncomeList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { kind } = useParams();
  const cfg = KIND[kind] || KIND["tam-tinh"];

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterMode, setFilterMode] = useState("DATE");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [month, setMonth] = useState(moment().format("YYYY-MM"));
  const [quarter, setQuarter] = useState(`Q${Math.floor(moment().month() / 3) + 1}-${moment().year()}`);
  const [sortKey, setSortKey] = useState("NEWEST");

  const [stat, setStat] = useState({});
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useDisclosure();
  const [sheetType, setSheetType] = useState(null); // MODE | SORT | QUARTER

  const sortOption = SORT_OPTIONS.find((x) => x.key === sortKey) || SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => {
      const params = { page: p, limit: LIMIT, sortBy: sortOption.sortBy, sortOrder: sortOption.sortOrder };
      if (cfg.paymentStatus) params.paymentStatus = cfg.paymentStatus;
      if (filterMode === "DATE") {
        if (fromDate) params.startTime = fromDate;
        if (toDate) params.endTime = toDate;
      } else if (filterMode === "MONTH") {
        params.month = moment(month, "YYYY-MM").format("MM-YYYY");
      } else if (filterMode === "QUARTER") {
        params.quarterYear = quarter;
      }
      return params;
    },
    [cfg, sortOption, filterMode, fromDate, toDate, month, quarter]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      const params = buildParams(p);
      if (p === 1) {
        getIncomeStatisticApi(params).then((d) => { if (rid === reqId.current) setStat(d || {}); }).catch(() => {});
      }
      try {
        const data = await cfg.api(params);
        if (rid !== reqId.current) return;
        const list = data?.items || data || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setTotal(data?.pagination?.total ?? list.length);
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [cfg, buildParams]
  );

  useEffect(() => { fetchPage(1, "replace"); }, [fetchPage]);

  const openSheet = (t) => { setSheetType(t); sheet.onOpen(); };
  const modeLabel = FILTER_MODES.find((m) => m.key === filterMode)?.label || "Ngày";
  const quarterLabel = (() => { const m = quarter.match(/^Q([1-4])-(\d{4})$/); return m ? `Quý ${m[1]}/${m[2]}` : "Chọn quý"; })();

  const quarterOptions = (() => {
    const years = [moment().year(), moment().year() - 1, moment().year() - 2];
    const out = [];
    years.forEach((y) => [1, 2, 3, 4].forEach((q) => out.push({ key: `Q${q}-${y}`, label: `Quý ${q}/${y}` })));
    return out;
  })();

  const sheetItems = sheetType === "MODE" ? FILTER_MODES.map((m) => ({ key: m.key, label: m.label, raw: m.key }))
    : sheetType === "SORT" ? SORT_OPTIONS.map((s) => ({ key: s.key, label: s.label, raw: s.key }))
    : quarterOptions.map((q) => ({ key: q.key, label: q.label, raw: q.key }));
  const isActive = (raw) => (sheetType === "MODE" ? raw === filterMode : sheetType === "SORT" ? raw === sortKey : raw === quarter);
  const pick = (raw) => { if (sheetType === "MODE") setFilterMode(raw); else if (sheetType === "SORT") setSortKey(raw); else setQuarter(raw); sheet.onClose(); };

  const FilterField = ({ label, value, onClick, w = "48%" }) => (
    <Box w={w}>
      <Text fontSize="12px" color={SUBTLE} mb="4px">{label}</Text>
      <Flex as="button" type="button" onClick={onClick} align="center" justify="space-between" h="40px" px="10px" bg="#F7F9FC" borderRadius="8px" w="100%">
        <Text fontSize="13px" color={TEXT} noOfLines={1} textAlign="left">{value}</Text>
        <img src={appImages.icon_chevron_down} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
      </Flex>
    </Box>
  );

  const DateFilterField = ({ label, value, onChange, w = "48%" }) => (
    <Box w={w}>
      <Text fontSize="12px" color={SUBTLE} mb="4px">{label}</Text>
      <Box position="relative" w="100%">
        <Flex align="center" justify="space-between" h="40px" px="10px" bg="#F7F9FC" borderRadius="8px">
          <Text fontSize="13px" color={value ? TEXT : MUTED}>{value ? moment(value, "YYYY-MM-DD").format("DD/MM/YY") : "Chọn ngày"}</Text>
          <img src={appImages.icon_chevron_down} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
        </Flex>
        <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} position="absolute" inset="0" w="100%" h="100%" opacity={0} p="0" cursor="pointer" />
      </Box>
    </Box>
  );

  const extras = (
    <>
      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">{sheetType === "MODE" ? "Bộ lọc thời gian" : sheetType === "SORT" ? "Sắp xếp" : "Quý giao dịch"}</Text>
            <Box maxH="55vh" overflowY="auto">
              {sheetItems.map((it, i, arr) => {
                const active = isActive(it.raw);
                return (
                  <Flex key={it.key} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={i === arr.length - 1 ? "none" : "1px solid #EEF1F6"} onClick={() => pick(it.raw)}>
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400} textAlign="left">{it.label}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => {
      const src = cfg.provisional ? it?.locking : it;
      const ui = incomeStatusUi(src);
      return {
        id: it._id,
        ui,
        title: cfg.provisional
          ? `${it?.projectName || ""}${it?.projectName ? " - " : ""}${it?.apartmentCode || ""}`.trim() || "-"
          : `${it?.project?.name || ""}${it?.project?.name ? " - " : ""}${it?.apartment?.code || ""}`.trim() ||
            "-",
        date: fmtD(cfg.provisional ? it?.locking?.bookingTime : it?.bookingTime),
        customer: (cfg.provisional ? it?.customerName : it?.customer?.name) || "-",
        kRate: fmtK(it?.kRate),
        totalValue: formatVnd(it?.totalValue || 0),
        fee: formatVnd(
          (cfg.provisional ? it?.estimatedServiceFeePrice : it?.priceForCommission) || 0,
        ),
      };
    });

    return (
      <DesktopPage title={cfg.title}>
        <DesktopStatRow
          items={[
            { key: "summary", title: cfg.summaryText, total: formatMoney(cfg.summaryField(stat) || 0) },
            ...cfg.stats.map((st) => ({
              key: st.sub,
              title: st.title,
              total:
                st.kind === "price"
                  ? formatMoney(st.get(stat, total) || 0)
                  : String(st.get(stat, total) || 0),
            })),
          ]}
        />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <FilterField
              label="Bộ lọc thời gian"
              value={modeLabel}
              onClick={() => openSheet("MODE")}
              w="220px"
            />
            {filterMode === "DATE" && (
              <>
                <DateFilterField label="Từ ngày" value={fromDate} onChange={setFromDate} w="180px" />
                <DateFilterField label="Đến ngày" value={toDate} onChange={setToDate} w="180px" />
              </>
            )}
            {filterMode === "MONTH" && (
              <Box w="220px">
                <Text fontSize="12px" color={SUBTLE} mb="4px">
                  Tháng giao dịch
                </Text>
                <Input
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  h="40px"
                  bg="#F7F9FC"
                  borderRadius="8px"
                  fontSize="13px"
                />
              </Box>
            )}
            {filterMode === "QUARTER" && (
              <FilterField
                label="Quý giao dịch"
                value={quarterLabel}
                onClick={() => openSheet("QUARTER")}
                w="220px"
              />
            )}
            <Box w="200px">
              <Text fontSize="12px" color={SUBTLE} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="10px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="13px"
                onClick={() => openSheet("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có dữ liệu"
            onRowClick={(row) =>
              navigate(`${EMP_BASE_PATH}/tai-chinh/thu-nhap/${kind}/${row.id}`)
            }
            rows={rows}
            headers={[
              { key: "title", label: "Dự án / Căn hộ", align: "left", width: "22%" },
              { key: "date", label: "Thời gian", width: "12%" },
              { key: "customer", label: "Khách hàng", align: "left", width: "16%" },
              { key: "kRate", label: "Hệ số K", width: "9%" },
              {
                key: "totalValue",
                label: "Giá trị niêm yết",
                align: "right",
                width: "15%",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={APP.primary200}>
                    {row.totalValue}
                  </Text>
                ),
              },
              {
                key: "fee",
                label: cfg.provisional ? "Giá PDV tạm tính" : "Giá PDV",
                align: "right",
                width: "14%",
              },
              {
                key: "status",
                label: "Trạng thái",
                width: "12%",
                render: (row) => (
                  <Box display="inline-block" bg={row.ui.bg} borderRadius="6px" px="10px" py="3px">
                    <Text fontSize="12px" fontWeight={600} color={row.ui.color}>
                      {row.ui.label}
                    </Text>
                  </Box>
                ),
              },
            ]}
          />

          {hasNext && (
            <Flex
              as="button"
              onClick={() => fetchPage(page + 1, "append")}
              justify="center"
              py="16px"
              w="100%"
              color={APP.primary200}
              fontWeight={600}
              fontSize="14px"
            >
              {loading ? <Spinner size="sm" /> : "Xem thêm"}
            </Flex>
          )}
        </DesktopPanel>

        {extras}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="#F6F8FC" minH="100dvh" pb="40px">
      <AppBar title={cfg.title} onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/thu-nhap`)} />

      <Box px="16px" pt="12px">
        {/* Bộ lọc chi tiết */}
        <Flex as="button" w="100%" h="44px" px="12px" align="center" justify="space-between" bg="white" border="1px solid" borderColor="#E5E9F0" borderTopRadius="12px" borderBottomRadius={filterOpen ? "0" : "12px"} borderBottom={filterOpen ? "0" : "1px solid"} borderBottomColor="#E5E9F0" onClick={() => setFilterOpen((p) => !p)}>
          <Flex align="center" gap="8px">
            <img src={appImages.icon_filter_detail} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
            <Text fontSize="14px" fontWeight={600} color={TEXT}>Bộ lọc chi tiết</Text>
          </Flex>
          <img src={filterOpen ? appImages.icon_chevron_up : appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
        </Flex>
        <Collapse in={filterOpen} animateOpacity>
          <Box bg="white" border="1px solid" borderColor="#E5E9F0" borderTop="0" borderBottomRadius="12px" p="12px">
            <Flex wrap="wrap" gap="10px">
              <FilterField label="Bộ lọc thời gian" value={modeLabel} onClick={() => openSheet("MODE")} w={filterMode !== "DATE" ? "48%" : "100%"} />
              {filterMode === "DATE" && (
                <>
                  <DateFilterField label="Từ ngày" value={fromDate} onChange={setFromDate} />
                  <DateFilterField label="Đến ngày" value={toDate} onChange={setToDate} />
                </>
              )}
              {filterMode === "MONTH" && (
                <Box w="48%">
                  <Text fontSize="12px" color={SUBTLE} mb="4px">Tháng giao dịch</Text>
                  <Box position="relative" w="100%">
                    <Flex align="center" justify="space-between" h="40px" px="10px" bg="#F7F9FC" borderRadius="8px">
                      <Text fontSize="13px" color={TEXT}>{month ? `Tháng ${moment(month, "YYYY-MM").format("MM/YYYY")}` : "Chọn tháng"}</Text>
                      <img src={appImages.icon_chevron_down} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
                    </Flex>
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} position="absolute" inset="0" w="100%" h="100%" opacity={0} p="0" cursor="pointer" />
                  </Box>
                </Box>
              )}
              {filterMode === "QUARTER" && (
                <FilterField label="Quý giao dịch" value={quarterLabel} onClick={() => openSheet("QUARTER")} />
              )}
            </Flex>
          </Box>
        </Collapse>

        <Box h="12px" />
        <SummaryCard text={cfg.summaryText} total={formatMoney(cfg.summaryField(stat) || 0)} />

        <Flex wrap="wrap" justify="space-between" gap="12px" mt="12px">
          {cfg.stats.map((s) => (
            <StatCard key={s.sub} sub={s.sub} title={s.title} type={s.type} value={s.kind === "price" ? formatMoney(s.get(stat, total) || 0) : String(s.get(stat, total) || 0)} />
          ))}
        </Flex>

        <Flex mt="16px" mb="14px" align="center" justify="space-between">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Danh sách giao dịch</Text>
          <Flex as="button" align="center" gap="6px" onClick={() => openSheet("SORT")}>
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{sortOption.label}</Text>
            <img src={appImages.ion_sort_desc} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
          </Flex>
        </Flex>

        {loading && !items.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="24px">Không có dữ liệu</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <IncomeCard key={it._id} item={it} provisional={cfg.provisional} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/thu-nhap/${kind}/${it._id}`)} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      {extras}
    </Box>
  );
};

export default IncomeList;
